import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2,
  Users,
  UserCheck,
  Plug,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Circle,
  FileBarChart,
  ArrowRight,
  RefreshCw,
  ShieldAlert,
} from "lucide-react";

/**
 * v1.2 PR-B.1 — Super-admin overview workspace landing.
 *
 *  - 4 KPI tiles (orgs, users, active visits cross-org, integration health)
 *  - 1-line system status banner
 *  - Recent activity feed (last 20 cross-org audit rows, refreshed every 60s)
 *
 * Backed by:
 *   GET /api/super-admin/overview/summary
 *   GET /api/super-admin/overview/recent-activity?limit=20
 */
export default function SuperAdminOverview() {
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async (background = false) => {
    if (background) setRefreshing(true); else setLoading(true);
    try {
      const [s, a] = await Promise.all([
        api.get("/super-admin/overview/summary"),
        api.get("/super-admin/overview/recent-activity", { params: { limit: 20 } }),
      ]);
      setSummary(s.data);
      setActivity(a.data || []);
    } catch (e) {
      if (!background) toast.error(formatApiError(e));
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    timerRef.current = setInterval(() => load(true), 60_000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  return (
    <SuperAdminLayout>
      <div data-testid="super-admin-overview-page" className="space-y-6">
        <header className="flex items-end justify-between gap-4">
          <div>
            <div className="eb-overline mb-1">Super admin</div>
            <h1 className="text-3xl font-semibold text-navy-900 dark:text-white">Overview</h1>
            <p className="text-sm text-navy-500 dark:text-navy-300 mt-1">
              Cross-org health, vault status, and the last 20 actions across every organisation.
            </p>
          </div>
          <button
            onClick={() => load(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border border-navy-200 dark:border-navy-600 text-navy-700 dark:text-navy-100 hover:bg-navy-50 dark:hover:bg-navy-700"
            data-testid="overview-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </header>

        {loading ? (
          <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl p-8 text-sm text-navy-500 dark:text-navy-300">
            Loading overview…
          </div>
        ) : !summary ? (
          <div className="bg-white dark:bg-navy-800 border border-danger/30 rounded-2xl p-6 text-sm text-danger">
            Unable to load overview. Refresh to retry.
          </div>
        ) : (
          <>
            <SystemStatusBar summary={summary.system} />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiTile
                label="Organisations"
                value={summary.orgs}
                icon={Building2}
                href="/super-admin"
                testid="kpi-orgs"
              />
              <KpiTile
                label="Total users"
                value={summary.users}
                icon={Users}
                testid="kpi-users"
              />
              <KpiTile
                label="Active visits"
                value={summary.active_visits}
                icon={UserCheck}
                hint="signed-in, not yet signed-out"
                testid="kpi-active-visits"
              />
              <VaultHealthTile integrations={summary.integrations || []} />
            </div>

            {/* v2 PR-1 Feature 2 — Orgs at risk tile (sits beneath the KPI strip). */}
            <OrgsAtRiskCard />

            <RecentActivityCard rows={activity} refreshing={refreshing} />
          </>
        )}
      </div>
    </SuperAdminLayout>
  );
}

function SystemStatusBar({ summary }) {
  // v1.2 PR-B.2 — three-state banner driven by cron telemetry. Clickable.
  const level = summary?.status_level || "green";
  const cls = {
    green:  "bg-success/10 text-success border-success/30",
    amber:  "bg-warning/10 text-warning border-warning/30",
    red:    "bg-danger/10  text-danger  border-danger/30",
  }[level];
  const Icon = level === "green" ? CheckCircle2 : AlertTriangle;
  const failures = summary?.recent_failures_7d || 0;
  const text = summary?.summary
    || (failures === 0 ? "All systems healthy"
                       : `${failures} cron failure(s) in the last 7 days — review Maintenance`);
  return (
    <Link
      to="/super-admin/maintenance"
      data-testid="system-status-bar"
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border hover:opacity-90 transition-opacity ${cls}`}
    >
      <Icon className="w-4 h-4" />
      {text}
    </Link>
  );
}

function KpiTile({ label, value, icon: Icon, hint, href, testid }) {
  const inner = (
    <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl p-5 h-full flex flex-col justify-between hover:shadow-card transition-shadow" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="eb-overline">{label}</div>
        <Icon className="w-5 h-5 text-orange-500" />
      </div>
      <div>
        <div className="text-4xl font-semibold text-navy-900 dark:text-white mt-2 tabular-nums">
          {value?.toLocaleString?.() ?? value ?? "—"}
        </div>
        {hint && <div className="text-xs text-navy-400 dark:text-navy-300 mt-1">{hint}</div>}
      </div>
    </div>
  );
  return href ? <Link to={href}>{inner}</Link> : inner;
}

function VaultHealthTile({ integrations }) {
  const pill = (status) => {
    if (status === "green") return { cls: "bg-success/10 text-success border-success/30", Icon: CheckCircle2 };
    if (status === "amber") return { cls: "bg-warning/10 text-warning border-warning/30", Icon: AlertTriangle };
    if (status === "red") return { cls: "bg-danger/10 text-danger border-danger/30", Icon: XCircle };
    return { cls: "bg-navy-50 text-navy-500 border-navy-200", Icon: Circle };
  };
  return (
    <Link to="/super-admin/integrations">
      <div className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl p-5 h-full hover:shadow-card transition-shadow" data-testid="kpi-vault-health">
        <div className="flex items-start justify-between mb-3">
          <div className="eb-overline">Vault health</div>
          <Plug className="w-5 h-5 text-orange-500" />
        </div>
        <div className="space-y-1.5">
          {integrations.length === 0 ? (
            <div className="text-xs text-navy-400 dark:text-navy-300">No integrations configured.</div>
          ) : integrations.map((i) => {
            const { cls, Icon } = pill(i.status);
            return (
              <div
                key={i.integration_key}
                className={`flex items-center justify-between text-xs px-2 py-1.5 rounded border ${cls}`}
                data-testid={`vault-pill-${i.integration_key}`}
              >
                <span className="inline-flex items-center gap-1.5 font-medium">
                  <Icon className="w-3 h-3" /> {i.integration_key}
                </span>
                <span className="opacity-70 uppercase tracking-wide text-[10px]">{i.active_path || "—"}</span>
              </div>
            );
          })}
        </div>
      </div>
    </Link>
  );
}

function RecentActivityCard({ rows, refreshing }) {
  return (
    <section
      className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl overflow-hidden"
      data-testid="overview-recent-activity"
    >
      <header className="px-5 py-4 border-b border-navy-100 dark:border-navy-700 flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <FileBarChart className="w-4 h-4 text-orange-500" />
          <h2 className="font-semibold text-navy-900 dark:text-white">Recent activity (cross-org)</h2>
          {refreshing && <span className="text-xs text-navy-400 dark:text-navy-300 ml-2">refreshing…</span>}
        </div>
        <Link
          to="/super-admin/audit-log"
          className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
          data-testid="overview-view-full-audit"
        >
          View full audit log <ArrowRight className="w-3 h-3" />
        </Link>
      </header>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-sm text-navy-500 dark:text-navy-300 text-center">No activity yet.</div>
      ) : (
        <ul className="divide-y divide-navy-100 dark:divide-navy-700">
          {rows.map((r) => (
            <li key={r.id} className="px-5 py-3 flex items-start justify-between gap-3 hover:bg-navy-50/40 dark:hover:bg-navy-700/40 transition-colors" data-testid="overview-activity-row">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-navy-900 dark:text-white">
                  <span className="font-medium">{r.actor_email || "system"}</span>
                  <span className="text-navy-400 dark:text-navy-300"> · </span>
                  <span className="font-mono text-xs text-navy-700 dark:text-navy-200">{r.action}</span>
                  {r.target_type && (
                    <>
                      <span className="text-navy-400 dark:text-navy-300"> on </span>
                      <span className="text-xs text-navy-600 dark:text-navy-300">{r.target_type}</span>
                    </>
                  )}
                </div>
                <div className="text-xs text-navy-500 dark:text-navy-300 mt-0.5 truncate">
                  {r.org_name || "—"}
                </div>
              </div>
              <div className="text-xs text-navy-400 dark:text-navy-300 font-mono whitespace-nowrap">
                {r.at ? new Date(r.at).toLocaleString() : "—"}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ============================================================================
// v2 PR-1 Feature 2 — Orgs at risk card.
// ============================================================================

function riskTone(score) {
  if (score >= 100) return { pill: "bg-danger/10 text-danger border-danger/30",   label: "Critical" };
  if (score >= 50)  return { pill: "bg-warning/10 text-warning border-warning/30", label: "Elevated" };
  return                     { pill: "bg-navy-100 dark:bg-navy-700 text-navy-700 dark:text-navy-200 border-navy-200 dark:border-navy-600", label: "Low" };
}

function summariseSignals(signals) {
  // Compact, human-readable join: "Past-due subscription · 3 cron failures · 2 expiring insurances"
  const buckets = new Map();
  for (const s of signals || []) {
    const k = s.kind;
    buckets.set(k, (buckets.get(k) || 0) + 1);
  }
  const out = [];
  if (buckets.has("subscription_unpaid"))            out.push("Unpaid subscription");
  if (buckets.has("subscription_past_due"))          out.push("Past-due subscription");
  if (buckets.has("cron_failure_cluster"))           out.push("Cron failures");
  const insur = buckets.get("contractor_compliance_expiring") || 0;
  if (insur)                                          out.push(`${insur} expiring insurance${insur > 1 ? "s" : ""}`);
  if (buckets.has("resend_domain_unverified"))       out.push("Resend domain unverified");
  return out.join(" · ") || "—";
}

function OrgsAtRiskCard() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/super-admin/overview/orgs-at-risk?limit=10");
      setRows(data || []);
      setErr(null);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000); // 5 min refresh
    return () => clearInterval(t);
  }, [load]);

  return (
    <section
      className="bg-white dark:bg-navy-800 border border-navy-100 dark:border-navy-700 rounded-2xl overflow-hidden"
      data-testid="orgs-at-risk-card"
    >
      <header className="px-5 py-4 border-b border-navy-100 dark:border-navy-700 flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-orange-500" />
          <h2 className="font-semibold text-navy-900 dark:text-white">Orgs at risk</h2>
          <span className="text-xs text-navy-500 dark:text-navy-300">(top 10 · refreshes every 5m)</span>
        </div>
        <button
          onClick={load}
          className="text-xs px-2.5 py-1 rounded-md border border-navy-200 dark:border-navy-600 text-navy-600 dark:text-navy-200 hover:bg-navy-50 dark:hover:bg-navy-700 inline-flex items-center gap-1.5"
          data-testid="orgs-at-risk-refresh"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </header>
      {err && <div className="p-5 text-sm text-danger">{err}</div>}
      {!err && rows === null && (
        <div className="p-5 text-sm text-navy-500 dark:text-navy-300">Loading…</div>
      )}
      {!err && rows && rows.length === 0 && (
        <div className="p-8 text-center" data-testid="orgs-at-risk-empty">
          <CheckCircle2 className="w-10 h-10 mx-auto text-success-600 mb-2" />
          <div className="text-sm font-semibold text-navy-900 dark:text-white">All organisations healthy</div>
          <div className="text-xs text-navy-500 dark:text-navy-300 mt-1">No risk signals across the fleet.</div>
        </div>
      )}
      {!err && rows && rows.length > 0 && (
        <ul className="divide-y divide-navy-100 dark:divide-navy-700">
          {rows.map((r) => {
            const t = riskTone(r.risk_score);
            return (
              <li key={r.org_id} data-testid={`orgs-at-risk-row-${r.org_id}`}>
                <Link
                  to={`/super-admin?focus=${r.org_id}`}
                  className="flex items-center gap-4 px-5 py-3 hover:bg-navy-50 dark:hover:bg-navy-700 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy-900 dark:text-white truncate">{r.org_name}</div>
                    <div className="text-xs text-navy-500 dark:text-navy-300 mt-0.5 truncate">{summariseSignals(r.signals)}</div>
                  </div>
                  <div className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${t.pill}`}>
                    {r.risk_score} · {t.label}
                  </div>
                  <ArrowRight className="w-4 h-4 text-navy-300" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
